import React, { useState } from 'react';
import { Button } from 'antd';

/**
 * Phase 7C-4 -- the two screens the auth gate in `App.tsx` shows while
 * it does not yet know whether there is a session.
 *
 * WHY A GATE RATHER THAN A REDIRECT: routing to a page and then kicking
 * the user back out on a failed check would flash real shop data first.
 * The gate holds a small splash until the Backend has answered, so
 * protected UI is never built for an unverified session.
 */

/** Startup placeholder, in the app's existing visual language. */
export const AuthSplash: React.FC = () => (
    <div className='auth-gate' data-testid='auth-splash' role='status' aria-live='polite'>
        <div className='auth-gate-card'>
            <div className='auth-gate-spinner' aria-hidden='true' />
            <div className='auth-gate-title'>กำลังตรวจสอบการเข้าสู่ระบบ</div>
            <div className='auth-gate-desc'>กรุณารอสักครู่</div>
        </div>
    </div>
);

interface AuthUnreachableProps {
    /** Re-run SESSION_ME with the stored token. */
    onRetry: () => void | Promise<void>;
    /** Drop the local token and go to the login page. */
    onForget: () => void;
}

/**
 * Shown when the token could not be checked because the Backend was not
 * reachable. Says plainly that this is a connection problem -- not "your
 * session expired", which would be a guess. The token is still in this
 * tab; the user has not been signed out.
 */
export const AuthUnreachable: React.FC<AuthUnreachableProps> = ({ onRetry, onForget }) => {
    const [busy, setBusy] = useState(false);

    const retry = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await onRetry();
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className='auth-gate' data-testid='auth-unreachable' role='alert'>
            <div className='auth-gate-card'>
                <div className='auth-gate-icon' aria-hidden='true'>📡</div>
                <div className='auth-gate-title'>ไม่สามารถตรวจสอบการเข้าสู่ระบบได้</div>
                <div className='auth-gate-desc'>
                    เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ ระบบยังไม่ได้ออกจากระบบให้คุณ
                    กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ตแล้วลองใหม่อีกครั้ง
                </div>
                <div className='auth-gate-actions'>
                    <Button
                        className='btn-common'
                        type='primary'
                        onClick={retry}
                        loading={busy}
                        disabled={busy}
                        data-testid='btn-auth-retry'
                    >
                        ลองใหม่
                    </Button>
                    <Button
                        className='btn-cancel'
                        onClick={onForget}
                        disabled={busy}
                        data-testid='btn-auth-forget'
                    >
                        กลับไปหน้าเข้าสู่ระบบ
                    </Button>
                </div>
            </div>
        </div>
    );
};
